// Regras puras da câmera do quadro: conversão de coordenadas, zoom e níveis de detalhe.

export type Ponto = { x: number; y: number };
/** x e y = deslocamento da origem do quadro na tela, em pixels; zoom = escala. */
export type Camera = Ponto & { zoom: number };
export type NivelDetalhe = 'completo' | 'compacto' | 'minimo';
export type EventoRoda = { deltaX: number; deltaY: number; deltaMode: number; ctrlKey: boolean };

export const ZOOM_MINIMO = 0.1;
export const ZOOM_MAXIMO = 2;
const LIMIAR_ARRASTE_PX = 4;
const PRECISAO = 1e6;

const arredondar = (valor: number) => Math.round(valor * PRECISAO) / PRECISAO;

export function telaParaQuadro(ponto: Ponto, camera: Camera): Ponto {
  return { x: arredondar((ponto.x - camera.x) / camera.zoom), y: arredondar((ponto.y - camera.y) / camera.zoom) };
}

export function quadroParaTela(ponto: Ponto, camera: Camera): Ponto {
  return { x: arredondar(ponto.x * camera.zoom + camera.x), y: arredondar(ponto.y * camera.zoom + camera.y) };
}

export const limitarZoom = (zoom: number) => Math.min(ZOOM_MAXIMO, Math.max(ZOOM_MINIMO, zoom));

/** Novo zoom mantendo fixo, sob o cursor, o mesmo ponto do quadro (RQ-QDR-002). */
export function zoomEm(camera: Camera, cursor: Ponto, fator: number): Camera {
  const zoom = limitarZoom(camera.zoom * fator);
  const sobCursor = { x: (cursor.x - camera.x) / camera.zoom, y: (cursor.y - camera.y) / camera.zoom };
  return { x: cursor.x - sobCursor.x * zoom, y: cursor.y - sobCursor.y * zoom, zoom };
}

const ZOOM_FOCO = 0.75;
const FRACAO_FOCO = 2 / 5;

/** Aviso de post-it (RQ-NOT-011): 75% de zoom, com o centro do item a 2/5 da largura e da altura da tela. */
export function cameraNoFoco(item: Ponto & { largura: number; altura: number }, tela: { largura: number; altura: number }): Camera {
  const centro = { x: item.x + item.largura / 2, y: item.y + item.altura / 2 };
  return {
    x: arredondar(tela.largura * FRACAO_FOCO - centro.x * ZOOM_FOCO),
    y: arredondar(tela.altura * FRACAO_FOCO - centro.y * ZOOM_FOCO),
    zoom: ZOOM_FOCO,
  };
}

/**
 * Roda do mouse dá zoom; pinça (ctrl + roda) dá zoom; dois dedos no trackpad movem o quadro.
 * Mouse: deltas em linhas (deltaMode 1) ou "degraus" inteiros grandes no eixo vertical.
 */
export function interpretarRoda(evento: EventoRoda): 'zoom' | 'mover' {
  if (evento.ctrlKey || evento.deltaMode !== 0) return 'zoom';
  const degrauDeMouse = evento.deltaX === 0 && Number.isInteger(evento.deltaY) && Math.abs(evento.deltaY) >= 50;
  return degrauDeMouse ? 'zoom' : 'mover';
}

export const ehArraste = (inicio: Ponto, atual: Ponto) =>
  Math.hypot(atual.x - inicio.x, atual.y - inicio.y) > LIMIAR_ARRASTE_PX;

/** RQ-QDR-006: completo a partir de 60%, compacto de 30% a 60%, mínimo abaixo de 30%. */
export function nivelDetalhe(zoom: number): NivelDetalhe {
  if (zoom >= 0.6) return 'completo';
  return zoom >= 0.3 ? 'compacto' : 'minimo';
}
